// components/admin/PlanForm.tsx

import React, { useState } from "react";
import Input from "../../components/ui/Input";
import TextArea from "../../components/ui/Textarea";
import Button from "../../components/ui/Button";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Plan, PlanCreatePayload, PlanUpdatePayload } from "../../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faMoneyBillWave, faCalendarDays } from "@fortawesome/free-solid-svg-icons";

interface PlanFormProps {
  initialData?: Plan | null;
  onSubmit: (
    data: Omit<PlanCreatePayload, "channel_id"> | PlanUpdatePayload | any
  ) => Promise<void>;
  isLoading: boolean;
  formError: string | null;
  isEditMode?: boolean;
}

const PlanForm: React.FC<PlanFormProps> = ({
  initialData,
  onSubmit,
  isLoading,
  formError,
  isEditMode = false,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [markupPrice, setMarkupPrice] = useState<string>(
    initialData?.markup_price?.toString() ?? ""
  );
  const [discountedPrice, setDiscountedPrice] = useState<string | any>(
    initialData?.discounted_price?.toString() ?? null
  );
  const [validityDays, setValidityDays] = useState<string>(
    initialData?.validity_days?.toString() ?? ""
  );
  const [description, setDescription] = useState(
    initialData?.description || ""
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const numMarkupPrice = markupPrice === "" ? null : Number(markupPrice);
    const numDiscountedPrice = Number(discountedPrice);
    const numValidity = Number(validityDays);

    if (!name.trim()) newErrors.name = "Plan name is required.";

    if (
      markupPrice !== "" &&
      (isNaN(numMarkupPrice ?? NaN) ||
        (numMarkupPrice !== null && numMarkupPrice < 0))
    ) {
      newErrors.markupPrice =
        "Markup price must be a valid non-negative number, or empty.";
    }

    if (discountedPrice !== "" && numDiscountedPrice < 0) {
      newErrors.discountedPrice =
        "Valid non-negative discounted price is required.";
    } else if (
      numMarkupPrice !== null &&
      numDiscountedPrice >= numMarkupPrice
    ) {
      newErrors.discountedPrice =
        "Discounted price must be less than the markup price.";
    }

    if (
      validityDays === "" ||
      isNaN(numValidity) ||
      numValidity <= 0 ||
      !Number.isInteger(numValidity)
    ) {
      newErrors.validityDays =
        "Validity must be a whole number of days greater than 0.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!validateForm()) {
      console.log("Plan form validation failed.");
      return;
    }

    const formData = {
      name: name.trim(),
      markup_price:
        markupPrice === "" || isNaN(Number(markupPrice))
          ? null
          : Number(markupPrice),
      discounted_price: discountedPrice && Number(discountedPrice),
      validity_days: Number(validityDays),
      description: description.trim() || null,
    };

    console.log("Form Data:", formData);
    // remove discounted price if its empty and markup price is not empty
    if (
      typeof formData.markup_price === "number" &&
      !formData.discounted_price
    ) {
      delete formData.discounted_price;
    }
    onSubmit(formData);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Enhanced Form Container */}
      <form
        onSubmit={handleSubmit}
        className="bg-gradient-to-br from-dark-secondary via-dark-secondary to-dark-tertiary/20 p-8 rounded-xl shadow-2xl border border-dark-tertiary/60 backdrop-blur-sm"
      >
        {/* Plan Details Section */}
        <div className="space-y-6">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">1</span>
              </div>
              Plan Information
            </h2>
            <p className="text-text-secondary text-sm ml-11">Basic plan details and description</p>
          </div>

          <div className="ml-11 space-y-6">
            <Input
              label="Plan Name"
              id="plan_name"
              name="planName"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                delete errors.name;
              }}
              required
              disabled={isLoading}
              error={errors.name}
              maxLength={100}
              className="transition-all duration-200 focus:ring-2 focus:ring-golden-accent/20"
            />

            <TextArea
              label="Description (Optional)"
              id="description"
              name="description"
              value={description || ""}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              disabled={isLoading}
              maxLength={500}
              className="transition-all duration-200 focus:ring-2 focus:ring-golden-accent/20"
              placeholder="Describe what this plan offers to users..."
            />
          </div>
        </div>

        {/* Pricing Section */}
        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">2</span>
              </div>
              Pricing Configuration
            </h2>
            <p className="text-text-secondary text-sm ml-11">Set the original and discounted prices</p>
          </div>

          <div className="ml-11">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="relative">
                <Input
                  label="Markup Price (Required)"
                  id="markup_price"
                  name="markup_price"
                  type="number"
                  value={markupPrice}
                  onChange={(e) => {
                    setMarkupPrice(e.target.value);
                    delete errors.markupPrice;
                    delete errors.discountedPrice;
                  }}
                  required
                  min={0}
                  disabled={isLoading}
                  error={errors.markupPrice}
                  placeholder="e.g., 1000.00"
                  className="transition-all duration-200 focus:ring-2 focus:ring-golden-accent/20"
                />
                <div className="mt-2 text-xs text-text-secondary bg-dark-tertiary/30 p-3 rounded-lg flex items-center gap-2">
                  <FontAwesomeIcon icon={faLightbulb} className="h-4 w-4 text-golden-accent" />
                  <span>This is the original price customers will see</span>
                </div>
              </div>

              <div className="relative">
                <Input
                  label="Discounted Price (Optional)"
                  id="discounted_price"
                  type="number"
                  name="discountedPrice"
                  value={discountedPrice}
                  onChange={(e) => {
                    setDiscountedPrice(e.target.value);
                    delete errors.discountedPrice;
                  }}
                  disabled={isLoading}
                  error={errors.discountedPrice}
                  placeholder="e.g., 800.00"
                  className="transition-all duration-200 focus:ring-2 focus:ring-golden-accent/20"
                />
                <div className="mt-2 text-xs text-text-secondary bg-dark-tertiary/30 p-3 rounded-lg flex items-center gap-2">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="h-4 w-4 text-golden-accent" />
                  <span>Leave empty for no discount, must be less than markup price</span>
                </div>
              </div>
            </div>

            {/* Price Preview */}
            {markupPrice && (
              <div className="mt-6 p-4 bg-dark-tertiary/20 rounded-lg border border-golden-accent/20">
                <h3 className="text-sm font-semibold text-text-primary mb-2">Price Preview:</h3>
                <div className="flex items-center space-x-4">
                  {discountedPrice && Number(discountedPrice) < Number(markupPrice) ? (
                    <>
                      <span className="text-2xl font-bold text-golden-accent">
                        ₹{Number(discountedPrice).toFixed(2)}
                      </span>
                      <span className="text-lg text-text-secondary line-through">
                        ₹{Number(markupPrice).toFixed(2)}
                      </span>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full font-semibold">
                        {Math.round(((Number(markupPrice) - Number(discountedPrice)) / Number(markupPrice)) * 100)}% OFF
                      </span>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-golden-accent">
                      ₹{Number(markupPrice).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Validity Section */}
        <div className="space-y-6 mt-8">
          <div className="pb-4 border-b border-dark-tertiary/30">
            <h2 className="text-xl font-semibold text-text-primary mb-2 flex items-center">
              <div className="w-8 h-8 bg-golden-accent/10 rounded-full flex items-center justify-center mr-3">
                <span className="text-golden-accent font-bold text-sm">3</span>
              </div>
              Plan Duration
            </h2>
            <p className="text-text-secondary text-sm ml-11">Set how long this plan remains active</p>
          </div>

          <div className="ml-11">
            <div className="max-w-md">
              <Input
                label="Validity (Days)"
                id="validity_days"
                name="validityDays"
                type="number"
                value={validityDays}
                onChange={(e) => {
                  setValidityDays(e.target.value);
                  delete errors.validityDays;
                }}
                required
                min={1}
                step={1}
                disabled={isLoading}
                error={errors.validityDays}
                placeholder="e.g., 30"
                className="transition-all duration-200 focus:ring-2 focus:ring-golden-accent/20"
              />
              <div className="mt-2 text-xs text-text-secondary bg-dark-tertiary/30 p-3 rounded-lg flex items-center gap-2">
                <FontAwesomeIcon icon={faCalendarDays} className="h-4 w-4 text-golden-accent" />
                <span>Number of days the plan will be active after purchase</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Error */}
        {formError && (
          <div className="mt-8">
            <ErrorMessage
              message={formError}
              className="mb-0"
              title="Submission Error"
            />
          </div>
        )}

        {/* Minimal Submit Button */}
        <div className="mt-6">
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={isLoading || Object.keys(errors).length > 0}
            variant="primary"
          >
            {isEditMode ? "Update Plan" : "Create Plan"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PlanForm;